import { Injectable, inject } from "@angular/core";
import { ApiService } from "./api.service";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { PagedResults } from '../models/paged-results/paged-results.model';
import { LimitOffsetPagingMeta, Sort, Paging } from '../models/paged-results/limit-offset-paging.model';
import { ProviderDataReview } from "../models/provider-data-review.model";
import { ProviderDataReviewFilters } from "../models/user-settings/provider-data-review-settings.model";

export class PagedProviderDataReviews implements PagedResults {
  private providerDataReviews: ProviderDataReview[];
  meta: LimitOffsetPagingMeta;

  get records() {
    return this.providerDataReviews;
  }
}

interface ProviderDataReviewQueueCounts {
  providerFaxNumberDataReviews: number,
  providerSpecialtyDataReviews: number
}

@Injectable()
export class ProviderDataReviewsService extends ApiService {
  private http = inject(HttpClient);

  getList(
    paging: Paging,
    filters: ProviderDataReviewFilters = null,
    sort: Sort
  ): Observable<PagedProviderDataReviews> {
    const searchParams = new URLSearchParams(`page=${paging.page}&per_page=${paging.pageSize}`);

    if (filters) {
      if (filters.reviewType && filters.reviewType.length > 0) {
        filters.reviewType.forEach(v => searchParams.append('type[]', v.toString()));
      }

      if (filters.reviewStatus && filters.reviewStatus.length > 0) {
        filters.reviewStatus.forEach(v => searchParams.append('status[]', v.toString()));
      }

      if (filters.assignedToIds && filters.assignedToIds.length > 0) {
        filters.assignedToIds.forEach(v => searchParams.append('assigned_to_ids[]', v.toString()));
      }
    }

    if (sort.col) {
      searchParams.append('sort_col', sort.col);

      if (sort.dir) {
        searchParams.append('sort_dir', sort.dir);
      }
    }

    return this.http.get<PagedProviderDataReviews>(
      `${environment.captureApi.url}/provider_data_reviews?${searchParams.toString()}`
    ).pipe(
      map(result => Object.assign(new PagedProviderDataReviews(), result))
    );
  }

  getMyQueueCounts(): Observable<ProviderDataReviewQueueCounts> {
    return this.http.get<ProviderDataReviewQueueCounts>(`${environment.captureApi.url}/provider_data_reviews/my_queue_counts`)
  }

  getMyQueue(page: number, pageSize: number): Observable<PagedProviderDataReviews> {
    return this.http.get<PagedProviderDataReviews>(
      `${environment.captureApi.url}/provider_data_reviews/my_queue?page=${page}&per_page=${pageSize}`
    ).pipe(
      map(result => Object.assign(new PagedProviderDataReviews(), result))
    );
  }

  // assignedToId is -1 to remove the assignment
  assignTo(
    providerDataReview: ProviderDataReview,
    assignToId: number
  ): Observable<ProviderDataReview> {
    return this.http.put<ProviderDataReview>(
      `${environment.captureApi.url}/provider_data_reviews/${providerDataReview.id}/assign_to`,
      { assign_to_id: assignToId }
    );
  }

  // assignedToId is -1 to remove the assignment
  batchAssignedTo(
    providerDataReviewIds: number[],
    assignToId: number
  ): Observable<any> {
    return this.http.post(
      `${environment.captureApi.url}/provider_data_reviews/batch_assign_to`,
      {
        assign_to_id: assignToId,
        ids: providerDataReviewIds
      }
    );
  }
}
